import {
  ACTION,
  ANALYTICS_URL,
  GA4_BOOST_EVENT,
  INTEGRATION_STATUS_ACTIVE,
  USER_ACTION,
} from '../../analytic/constants';

export const INTEGRATION_NAME_GA4 = 'ga4';

/**
 * Checks whether GA4 integration is active for the current shop.
 * Reads from `currentAppIntegration.analytics === 'ga4'` and
 * `extraFeatures.ga4.status === 'active'`.
 *
 * @returns {boolean}
 */
export const isGa4IntegrationActive = () => {
  const env = window.boostWidgetIntegration?.env;
  const appIntegration =
    window.boostWidgetIntegration?.app?.[env]?.templateSettings?.currentAppIntegration;

  if (!appIntegration || appIntegration.analytics !== INTEGRATION_NAME_GA4) return false;

  return appIntegration.extraFeatures?.ga4?.status === INTEGRATION_STATUS_ACTIVE;
};

/**
 * Returns the GA4 Measurement ID from the shop config, or empty string.
 *
 * @returns {string}
 */
export const getGa4MeasurementIdFromConfig = () => {
  const env = window.boostWidgetIntegration?.env;
  const appIntegration =
    window.boostWidgetIntegration?.app?.[env]?.templateSettings?.currentAppIntegration;

  return appIntegration?.extraFeatures?.ga4?.measurementId || '';
};

/**
 * Extracts the GA4 browser client_id from the `_ga` cookie.
 * Format: `GA1.1.XXXXXXXXXX.YYYYYYYYYY` → `XXXXXXXXXX.YYYYYYYYYY`
 *
 * @returns {string} client_id or empty string if not available
 */
export const getGa4ClientIdFromCookie = () => {
  try {
    const match = document.cookie.match(/_ga=GA\d+\.\d+\.([^;]+)/);
    return match ? match[1].trim() : '';
  } catch {
    return '';
  }
};

/**
 * Returns the current page URL for GA4 `page_location`.
 *
 * @returns {string}
 */
export const getGa4PageLocation = () => {
  try {
    return window.location.href || '';
  } catch {
    return '';
  }
};

/**
 * Resolves the GA4 client_id asynchronously.
 *
 * Priority:
 * 1. `_ga` cookie (synchronous, zero latency)
 * 2. `gtag('get', measurementId, 'client_id', cb)` — used when the cookie is
 *    absent (e.g. cookie blocked, first-party subdomain setup, or gtag.js
 *    not yet written the cookie).
 * 3. Empty string fallback after a 1 s timeout or if `gtag` is unavailable.
 *
 * @param {string} measurementId - GA4 Measurement ID (e.g. 'G-XXXXXXXX')
 * @returns {Promise<string>}
 */
const resolveGa4ClientId = (measurementId) => {
  const fromCookie = getGa4ClientIdFromCookie();
  if (fromCookie) return Promise.resolve(fromCookie);

  return new Promise((resolve) => {
    try {
      if (typeof window.gtag !== 'function' || !measurementId) {
        resolve('');
        return;
      }
      const timeout = setTimeout(() => resolve(''), 1000);
      window.gtag('get', measurementId, 'client_id', (clientId) => {
        clearTimeout(timeout);
        resolve(typeof clientId === 'string' ? clientId : '');
      });
    } catch {
      resolve('');
    }
  });
};

/**
 * Maps a Boost analytic event to the corresponding GA4 event name.
 *
 * @param {Record<string, unknown>} event - Boost analytic data object
 * @returns {string} GA4 event name or empty string if no mapping
 */
const mapEventToGa4EventName = (event) => {
  switch (event.u) {
    case USER_ACTION.VIEW_PRODUCT:
      if (event.a === ACTION.RECOMMEND && event.wid) {
        return GA4_BOOST_EVENT.PRODUCT_CLICK;
      }
      return GA4_BOOST_EVENT.PRODUCT_CLICK;

    case USER_ACTION.QUICK_VIEW:
      return GA4_BOOST_EVENT.PRODUCT_CLICK;

    case USER_ACTION.ADD_TO_CART:
      return GA4_BOOST_EVENT.ADD_TO_CART;

    case USER_ACTION.BUY_NOW:
      return GA4_BOOST_EVENT.BUY_NOW;

    case USER_ACTION.ATC_PRE_ORDER:
      return GA4_BOOST_EVENT.PRE_ORDER;

    default:
      return '';
  }
};

/**
 * Builds the `ga4_info` object to attach to the analytics event payload
 * sent to the Lambda. The Lambda uses this to forward the event to GA4
 * via the Measurement Protocol (server-side).
 *
 * @param {Record<string, unknown>} event - Boost analytic data object (from buildGeneralData)
 * @returns {{ integration_name: string, event_name: string, measurement_id: string, client_id: string }}
 */
export const getGa4Info = (event) => {
  const eventName = mapEventToGa4EventName(event);
  const measurementId = getGa4MeasurementIdFromConfig();
  const clientId = getGa4ClientIdFromCookie();

  return {
    integration_name: INTEGRATION_NAME_GA4,
    event_name: eventName,
    measurement_id: measurementId,
    client_id: clientId,
  };
};

/**
 * Sends a GA4-only event directly to the Lambda endpoint (`POST /e`).
 *
 * Use this for events that do NOT go through the normal `sendEvent()` click
 * flow (e.g. filter clicks, search queries, widget views, notify-me). The
 * Lambda reads `ga4_info.integration_name === 'ga4'` and forwards the event
 * to GA4 Measurement Protocol server-side — no `apiSecret` in the client.
 *
 * @param {string} eventName - One of the GA4_BOOST_EVENT values
 * @param {Record<string, unknown>} [eventFields] - Top-level Lambda payload
 *   fields for this event (e.g. `{ qs, filter_option_value, wid, source, pid }`)
 * @returns {Promise<void>}
 */
export const sendGa4EventToLambda = async (eventName, eventFields = {}) => {
  const isDebug = new URLSearchParams(window.location.search).get('boost_ga4_debug') === '1';

  if (isDebug) {
    console.log(`[Boost GA4] ▶ ${eventName}`, eventFields);
  }

  if (!isGa4IntegrationActive()) {
    if (isDebug) console.warn('[Boost GA4] ✗ skipped — GA4 integration not active');
    return;
  }

  const measurementId = getGa4MeasurementIdFromConfig();
  if (!measurementId) {
    if (isDebug) console.warn('[Boost GA4] ✗ skipped — measurement ID not configured');
    return;
  }

  const clientId = await resolveGa4ClientId(measurementId);
  if (!clientId) {
    if (isDebug) console.warn('[Boost GA4] ✗ skipped — client_id not available');
    return;
  }

  const payload = {
    tid: window.Shopify?.shop || '',
    sid: window.boostWidgetIntegration?.getSessionId?.() || '',
    t: new Date().toISOString(),
    ...eventFields,
    page_location: getGa4PageLocation(),
    ga4_info: {
      integration_name: INTEGRATION_NAME_GA4,
      event_name: eventName,
      measurement_id: measurementId,
      client_id: clientId,
    },
  };

  if (isDebug) {
    console.log(`[Boost GA4] → Lambda ${eventName}`, JSON.parse(JSON.stringify(payload)));
  }

  try {
    await fetch(ANALYTICS_URL, {
      method: 'POST',
      body: JSON.stringify(payload),
      // keepalive ensures the request completes even when the page navigates away
      // (e.g. search submit, View All, suggestion term click → full page load)
      keepalive: true,
    });
  } catch {
    // fire-and-forget — GA4 send failures must not affect storefront UX
  }
};
